import type {
  DoctorLicenseAdminApiOutContract,
  TreatmentCenterApiOutContract,
} from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import type { LicenseFormValues, LicenseEntry } from './LicenseFormSchema'

/** Strict version of the API license visit type with required fields */
export interface LicenseVisitType {
  VisitTypeId: string
  BaseVisibility: string | null
  VisibilityOverride: string | null
  TreatmentCenters: TreatmentCenterApiOutContract[]
}

/** Strict version of the API license with required fields */
export interface License {
  LicenseId: string
  Number: string
  StateCode: string
  DeaNumber: string | null
  DeaStateCode: string | null
  CslNumber: string | null
  VisitTypes: LicenseVisitType[]
}

export function transformApiLicense(apiLicense: DoctorLicenseAdminApiOutContract): License {
  const raw = apiLicense

  return {
    LicenseId: raw.LicenseId ?? '',
    Number: raw.Number ?? '',
    StateCode: raw.StateCode ?? '',
    DeaNumber: raw.DeaNumber ?? null,
    DeaStateCode: raw.DeaStateCode ?? null,
    CslNumber: raw.CslNumber ?? null,
    VisitTypes: (raw.VisitTypes ?? []).map((vt) => ({
      VisitTypeId: vt.VisitTypeId ?? '',
      BaseVisibility: vt.BaseVisibility ?? null,
      VisibilityOverride: vt.VisibilityOverride ?? null,
      TreatmentCenters: vt.TreatmentCenters ?? [],
    })),
  }
}

export function licenseToFormValues(license: License): LicenseFormValues {
  const entries: LicenseEntry[] = [{ type: 'Medical', number: license.Number, deaStateCode: '' }]

  if (license.CslNumber) {
    entries.push({ type: 'CSL', number: license.CslNumber, deaStateCode: '' })
  }

  if (license.DeaNumber) {
    entries.push({ type: 'DEA', number: license.DeaNumber, deaStateCode: license.DeaStateCode ?? '' })
  }

  return {
    stateCode: license.StateCode,
    licenseEntries: entries,
    visitTypes: license.VisitTypes.map((vt) => ({
      visitTypeId: vt.VisitTypeId,
      serviceName: '',
      visitName: '',
      isSelected: true,
      visibilityOverride: vt.VisibilityOverride ?? vt.BaseVisibility ?? 'all',
    })),
  }
}

export function formValuesToRequestBody(values: LicenseFormValues) {
  const medical = values.licenseEntries.find((e) => e.type === 'Medical')
  const csl = values.licenseEntries.find((e) => e.type === 'CSL')
  const dea = values.licenseEntries.find((e) => e.type === 'DEA')

  return {
    Number: medical?.number ?? '',
    StateCode: values.stateCode,
    DeaNumber: dea?.number || null,
    DeaStateCode: dea?.number ? dea.deaStateCode : null,
    CslNumber: csl?.number || null,
    VisitTypes: values.visitTypes
      .filter((vt) => vt.isSelected)
      .map((vt) => ({
        VisitTypeId: vt.visitTypeId,
        VisibilityType: vt.visibilityOverride,
      })),
  }
}
